import {createContext, useContext, useEffect, useMemo, useState, useTransition} from 'react';

import {FCWithChildren} from 'app/interface';

/**
 * Use this provider to determine that we are on the client side after hydration
 */
export const HydrationProviderContext = createContext<HydrationProviderContextProps>({hydrated: undefined});

export const HydrationProvider: FCWithChildren<HydrationProviderProps> = ({children, renderedRemotely}) => {
    const contextValue = useHydrationWithoutContext(renderedRemotely);
    return <HydrationProviderContext.Provider value={contextValue}>{children}</HydrationProviderContext.Provider>;
};

export function useHydrationWithoutContext(renderedRemotely: boolean) {
    const [hydrated, setHydrated] = useState(!renderedRemotely);
    const [, startTransition] = useTransition();
    const contextValue = useMemo(() => ({hydrated}), [hydrated]);

    useEffect(() => {
        if (renderedRemotely) {
            startTransition(() => {
                setHydrated(true);
            });
        }
    }, [renderedRemotely]);

    return contextValue;
}

export function useHydration(): HydrationProviderContextProps {
    return useContext<HydrationProviderContextProps>(HydrationProviderContext);
}

export function clientRender(hydrated: HydrationProviderContextProps['hydrated']) {
    const componentNotUsingSSR = hydrated === undefined;
    const secondRender = hydrated === true;
    return componentNotUsingSSR || secondRender;
}

interface HydrationProviderContextProps {
    hydrated: boolean | undefined;
}

interface HydrationProviderProps {
    renderedRemotely: boolean;
}
